/**
 * AUDIT / AUDIT-C (Alcohol Use Disorders Identification Test)
 * AUDIT-C: Q1–Q3 (0–12). Full AUDIT: Q1–Q10 (0–40).
 * Positive screen thresholds for Full AUDIT trigger:
 *   Male   AUDIT-C ≥ 5
 *   Female AUDIT-C ≥ 4
 */

export const AUDIT_C_QUESTIONS = {
  q1: {
    label: "How often do you have a drink containing alcohol?",
    options: [
      { label: "Never", value: 0 },
      { label: "Monthly or less", value: 1 },
      { label: "2–4 times per month", value: 2 },
      { label: "2–3 times per week", value: 3 },
      { label: "4 or more times per week", value: 4 },
    ],
  },
  q2: {
    label: "How many standard drinks do you have on a typical drinking day?",
    options: [
      { label: "1–2", value: 0 },
      { label: "3–4", value: 1 },
      { label: "5–6", value: 2 },
      { label: "7–9", value: 3 },
      { label: "10 or more", value: 4 },
    ],
  },
  q3: {
    label: "How often do you have 6 or more drinks on one occasion?",
    options: [
      { label: "Never", value: 0 },
      { label: "Less than monthly", value: 1 },
      { label: "Monthly", value: 2 },
      { label: "Weekly", value: 3 },
      { label: "Daily or almost daily", value: 4 },
    ],
  },
};

export const FULL_AUDIT_QUESTIONS = {
  q4: {
    label: "How often during the last year have you found that you were not able to stop drinking once you had started?",
    options: [
      { label: "Never", value: 0 },
      { label: "Less than monthly", value: 1 },
      { label: "Monthly", value: 2 },
      { label: "Weekly", value: 3 },
      { label: "Daily or almost daily", value: 4 },
    ],
  },
  q5: {
    label: "How often during the last year have you failed to do what was normally expected from you because of your drinking?",
    options: [
      { label: "Never", value: 0 },
      { label: "Less than monthly", value: 1 },
      { label: "Monthly", value: 2 },
      { label: "Weekly", value: 3 },
      { label: "Daily or almost daily", value: 4 },
    ],
  },
  q6: {
    label: "How often during the last year have you needed a first drink in the morning to get yourself going after a heavy drinking session?",
    options: [
      { label: "Never", value: 0 },
      { label: "Less than monthly", value: 1 },
      { label: "Monthly", value: 2 },
      { label: "Weekly", value: 3 },
      { label: "Daily or almost daily", value: 4 },
    ],
  },
  q7: {
    label: "How often during the last year have you had a feeling of guilt or remorse after drinking?",
    options: [
      { label: "Never", value: 0 },
      { label: "Less than monthly", value: 1 },
      { label: "Monthly", value: 2 },
      { label: "Weekly", value: 3 },
      { label: "Daily or almost daily", value: 4 },
    ],
  },
  q8: {
    label: "How often during the last year have you been unable to remember what happened the night before because you had been drinking?",
    options: [
      { label: "Never", value: 0 },
      { label: "Less than monthly", value: 1 },
      { label: "Monthly", value: 2 },
      { label: "Weekly", value: 3 },
      { label: "Daily or almost daily", value: 4 },
    ],
  },
  q9: {
    label: "Have you or somebody else been injured as a result of your drinking?",
    options: [
      { label: "No", value: 0 },
      { label: "Yes, but not in the last year", value: 2 },
      { label: "Yes, during the last year", value: 4 },
    ],
  },
  q10: {
    label: "Has a relative, friend, doctor, or other health professional expressed concern about your drinking, or suggested you cut down?",
    options: [
      { label: "No", value: 0 },
      { label: "Yes, but not in the last year", value: 2 },
      { label: "Yes, during the last year", value: 4 },
    ],
  },
};

/**
 * Returns true when the AUDIT-C score meets the threshold requiring full AUDIT questions.
 * @param {number|null} auditCScore
 * @param {"MALE"|"FEMALE"|string|undefined} gender
 */
export const needsFullAudit = (auditCScore, gender) => {
  if (auditCScore == null) return false;

  const isMale = gender?.toUpperCase() === "MALE";
  const threshold = isMale ? 5 : 4;

  return auditCScore >= threshold;
};

/**
 * @param {number|null} q1 - 0-4
 * @param {number|null} q2 - 0-4
 * @param {number|null} q3 - 0-4
 * @returns {number|null} total score 0-12, or null if any answer is missing
 */
export const calculateAuditC = (q1, q2, q3) => {
  if (q1 == null || q2 == null || q3 == null) return null;

  return Number(q1) + Number(q2) + Number(q3);
};

/**
 * @param {number|null} q1-q10 - individual question answers
 * @returns {number|null} total score 0-40, or null if any answer is missing
 */
export const calculateFullAudit = (q1, q2, q3, q4, q5, q6, q7, q8, q9, q10) => {
  if ([q1, q2, q3, q4, q5, q6, q7, q8, q9, q10].some((q) => q == null)) return null;

  return [q1, q2, q3, q4, q5, q6, q7, q8, q9, q10].reduce((acc, q) => acc + Number(q), 0);
};

/**
 * Returns which alcohol intervention section to show, based on the FULL AUDIT
 * score (0–40) — not AUDIT-C. Bands per HSE CDM Message Spec (local codes
 * X0334-0/X0333-0/X0332-0): 8–15 Increasing Risk, 16–19 Higher Risk,
 * 20+ Possible Dependence. Below 8 — or when no full AUDIT was completed
 * (AUDIT-C under threshold) — no brief intervention is required.
 * @param {string|number|null|undefined} fullAuditScore
 * @returns {"INCREASING_RISK"|"HIGHER_RISK"|"DEPENDENCE"|null}
 */
export const getAlcoholInterventionCategory = (fullAuditScore) => {
  const num = Number(fullAuditScore);

  if (!fullAuditScore || isNaN(num) || num < 8) return null;

  if (num <= 15) return "INCREASING_RISK";

  if (num <= 19) return "HIGHER_RISK";

  return "DEPENDENCE";
};

/**
 * @param {number} score - AUDIT-C score (0-12)
 * @param {"MALE"|"FEMALE"} gender
 * @returns {{ level: string, description: string, color: string }}
 */
export const getAuditCRiskLevel = (score, gender) => {
  const isMale = gender?.toUpperCase() === "MALE";
  const threshold = isMale ? 5 : 4;

  if (score < threshold) {
    return {
      level: "Low risk",
      description: "Score below threshold. No hazardous drinking indicated.",
      color: "green",
    };
  }

  if (score <= 7) {
    return {
      level: "Hazardous drinking",
      description: "Score above threshold. Brief intervention recommended.",
      color: "orange",
    };
  }

  return {
    level: "Harmful / possible dependence",
    description: "Full AUDIT-10 assessment recommended.",
    color: "red",
  };
};
